import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

// Brand logo: an open reconnaissance reticle ("recon") wrapping a reconcile
// checkmark. Single-path SVG art, one color, scales to a favicon. Theme-native:
// every color reads from the MUI theme (no hardcoded hex), the one exception
// being white art on the saturated gradient tile (DESIGN_SYSTEM.md section 2.8
// saturated-fill contrast text). Art geometry is on a 32x32 viewBox per
// Landing-Page-Spec.md section 1.

const RETICLE_ARCS = [
  'M16 3 A13 13 0 0 1 29 16', // top-right open arc
  'M16 29 A13 13 0 0 1 3 16', // bottom-left open arc
];
const RECONCILE_CHECK = 'M9.5 16.5 l4 4 L23 11';

/**
 * The mark on a tinted tile.
 *
 * variant:
 *   'gradient' (default) - indigo gradient tile (interactive -> accentDeep),
 *                          white art. The primary nav mark.
 *   'bar'                - v4 top-bar lockup tile (#1377, board): gradient
 *                          buttonPrimaryBg -> interactive, radius 6, no
 *                          shadow. Pair with favicon at 20px.
 *   'surface'            - surface.raised tile + hairline border, interactive
 *                          art. For placing the mark on a card.
 *   'mono'               - transparent tile, text.primary art. Single-color
 *                          contexts.
 * favicon: when true, drops the reticle arcs and keeps only the reconcile
 *   check (the spec's <=16px treatment).
 */
export const BrandMark = ({ size = 42, variant = 'gradient', favicon = false, sx, ...rest }) => {
  const theme = useTheme();
  const art = Math.round(size * 0.64); // 27px art inside a 42px tile, per spec ratio

  const tileByVariant = {
    gradient: {
      backgroundImage: `linear-gradient(135deg, ${theme.palette.interactive}, ${theme.palette.accentDeep})`,
      color: '#fff', // section 2.8 exception: contrast art on a saturated fill
      boxShadow: `0 6px 18px ${alpha(theme.palette.accentDeep, 0.4)}`,
    },
    bar: {
      backgroundImage: `linear-gradient(135deg, ${theme.palette.buttonPrimary.bg}, ${theme.palette.interactive})`,
      color: '#fff', // section 2.8 exception: contrast art on a saturated fill
      borderRadius: '6px',
    },
    surface: {
      backgroundColor: theme.palette.surface.raised,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.interactive,
    },
    mono: {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
    },
  };

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: `${Math.round(size * 0.28)}px`, // ~28% of size
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...tileByVariant[variant],
        ...sx,
      }}
      {...rest}
    >
      <Box
        component="svg"
        viewBox="0 0 32 32"
        fill="none"
        sx={{ width: art, height: art, display: 'block' }}
      >
        {!favicon &&
          RETICLE_ARCS.map((d) => (
            <path
              key={d}
              d={d}
              stroke="currentColor"
              strokeWidth="2.4"
              opacity="0.5"
              strokeLinecap="round"
            />
          ))}
        <path
          d={RECONCILE_CHECK}
          stroke="currentColor"
          strokeWidth="3.1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Box>
  );
};

/**
 * Wordmark: BUDGET in text.primary + RECON in the accent, run together. Optional
 * tagline beneath. Font family inherits; weight + tracking per spec. The v4
 * top-bar lockup passes `weight={600}` + `budgetColor` (bar.text) per the board
 * (11px, ls .16em, w600).
 */
export const Wordmark = ({ fontSize = 20, weight = 700, budgetColor, tagline = false, sx }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1, ...sx }}>
      <Box
        component="span"
        sx={{
          fontWeight: weight,
          fontSize,
          letterSpacing: '0.16em',
          color: budgetColor || theme.palette.text.primary,
          whiteSpace: 'nowrap',
        }}
      >
        BUDGET
        <Box component="span" sx={{ color: theme.palette.interactive }}>
          RECON
        </Box>
      </Box>
      {tagline && (
        <Box
          component="span"
          sx={{
            mt: 0.6,
            fontWeight: 600,
            fontSize: Math.max(9, Math.round(fontSize * 0.45)),
            letterSpacing: '0.34em',
            color: theme.palette.text.dim,
            whiteSpace: 'nowrap',
          }}
        >
          RECONCILE - VERIFY - CONTROL
        </Box>
      )}
    </Box>
  );
};

/**
 * Mark + wordmark lockup. Exposes a single accessible name ("BudgetRecon") and
 * hides the decorative art + split spans from assistive tech.
 */
export const BrandLockup = ({
  markSize = 42,
  fontSize = 20,
  markVariant = 'gradient',
  favicon = false,
  weight = 700,
  budgetColor,
  gap = 1.25,
  tagline = false,
  sx,
  ...rest
}) => (
  <Box
    role="img"
    aria-label="BudgetRecon"
    sx={{ display: 'inline-flex', alignItems: 'center', gap, ...sx }}
    {...rest}
  >
    <BrandMark size={markSize} variant={markVariant} favicon={favicon} />
    <Wordmark fontSize={fontSize} weight={weight} budgetColor={budgetColor} tagline={tagline} />
  </Box>
);

export default BrandLockup;
